import { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  DRAWER_BACKGROUND_RGB,
  containerVariants,
  itemVariants
} from "../../animations/animations";
import {
  DRAWER_ANIMATION_DELAY,
  DRAWER_HEIGHT,
  DRAWER_TRANSITITION,
  SCREEN_BREAKPOINTS
} from "../../constants/appConstants";
import { getDrawerOverflowStyle, sortByOrderBrand } from "../../helper/helper";
import styles from "./Drawer.module.scss";

interface ReusableDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  brandItems: any;
  routeItems: any;
  anchor?: "left" | "right" | "top" | "bottom";
}

const ReusableDrawer = ({
  isOpen,
  onClose,
  brandItems,
  routeItems,
  anchor = "right",
}: ReusableDrawerProps) => {
  const { t } = useTranslation();
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(
        () => setAnimateIn(true),
        DRAWER_ANIMATION_DELAY
      );
      return () => clearTimeout(timer);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  const isSmallScreen = window?.innerWidth < SCREEN_BREAKPOINTS.SMALL;
  const drawerHeight = isSmallScreen ? DRAWER_HEIGHT.OTHER_LAYOUT_HEIGHT : "";

  const drawerStyle = {
    overflow: getDrawerOverflowStyle(brandItems?.length || 0),
    height: drawerHeight
  };

  useEffect(() => {
    const container = document.getElementById("scrollable-container");

    if (container) {
      container.classList.toggle(styles.hideScrollbar, isOpen);
    }

    return () => {
      if (container) {
        container.classList.remove(styles.hideScrollbar);
      }
    };
  }, [isOpen]);

  return (
    <Drawer
      transitionDuration={DRAWER_TRANSITITION}
      disableScrollLock={false}
      anchor={anchor}
      open={isOpen}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: {
            xs: DRAWER_BACKGROUND_RGB.XS,
            sm: DRAWER_BACKGROUND_RGB.SM
          }
        }
      }}
      ModalProps={{
        disablePortal: true
      }}
      PaperProps={{
        className: styles.drawerPaper
      }}
    >
      <div className={styles.fullDrawerContent} style={drawerStyle}>
        <motion.div
          variants={containerVariants}
          className={styles.drawerContent}
          initial="hidden"
          animate={animateIn ? "show" : "hidden"}
        >
          <motion.div variants={itemVariants}>
            <p className={styles.brandsTitle}>{t("header.ourBrands")}</p>
          </motion.div>

          <List className={styles.drawerList}>
            {sortByOrderBrand(brandItems || []).map((item: any) => (
              <motion.div key={item.id} variants={itemVariants}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={item?.onClick}
                    className={`${styles.item}`}
                  >
                    <ListItemText primary={item?.label} />
                  </ListItemButton>
                </ListItem>
              </motion.div>
            ))}
          </List>

          <List className={styles.drawerList_path}>
            {routeItems.map((item: any, index: number) => {
              const isFirst = index === 0;
              const isLast = index === routeItems.length - 1;

              const classNames = [
                styles.item,
                styles.routeItem,
                isFirst ? styles.routeItemFirst : "",
                isLast ? styles.routeItemLast : ""
              ]
                .join(" ")
                .trim();

              return (
                <motion.div key={index} variants={itemVariants}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={item?.onClick}
                      className={classNames}
                    >
                      <ListItemText primary={item?.label} />
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              );
            })}
          </List>
        </motion.div>
      </div>
    </Drawer>
  );
};

export default ReusableDrawer;
